import { useState } from "react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import type { HttpError } from "@refinedev/core";
import { Button, DataTable, Modal, StatusBadge, type Column } from "@fawkes/ui";

import { ErrorState, LoadingState } from "../../components/states";
import type { Locale } from "../../i18n/messages";
import { formatDateTime } from "../portal/format";
import type { SelfServiceAccount, SelfServiceCredential, SelfServiceIssuedCredential } from "./types";

// jmix-9-2: the service-account self-service portal — the operator manages their OWN OAuth2 credentials
// (runbook service-account-self-service.md §2/§3). Three sections: Identity, Allowed IPs (READ-ONLY —
// "contact an administrator"), Credentials (rotate = mint a fresh credential → reveal-once → revoke the
// old one, no downtime). All reads/writes are self-scoped server-side (the target is derived from the
// JWT principal, never sent), so there is no account selector and no other-account surface.
//
// Reveal-once hygiene (T9-4): the minted plaintext secret lives in component state ONLY until copied,
// then it is dropped. The show-once dialog renders TRANSLATED labels (nl/en) — fixing the legacy
// SetServiceAccountView bug where it rendered raw `secretOnce.*` message keys.

function statusOf(error: unknown): number | undefined {
  return (error as HttpError | undefined)?.statusCode;
}

export function MijnServiceAccount() {
  const t = useTranslate();
  const locale = (useGetLocale()() ?? "nl") as Locale;
  const { data, isLoading, isError, refetch } = useCustom<SelfServiceAccount>({
    url: "portal/service-account",
    method: "get",
  });
  const { mutate, isLoading: mutating } = useCustomMutation();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [issued, setIssued] = useState<SelfServiceIssuedCredential | null>(null);
  const [secretCopied, setSecretCopied] = useState(false);
  const [error, setError] = useState<string | undefined>();
  // Copy errors are shown INSIDE the reveal-once dialog — the page-level `error` alert renders behind
  // the open modal, so a failed copy of the one-time secret would otherwise be silent.
  const [copyError, setCopyError] = useState<string | undefined>();

  if (isLoading) {
    return <LoadingState />;
  }
  if (isError) {
    return <ErrorState onRetry={() => void refetch()} />;
  }

  const account = data?.data ?? null;

  function mint() {
    setError(undefined);
    mutate(
      { url: "portal/service-account/credentials", method: "post", values: {} },
      {
        onSuccess: (res) => {
          setConfirmOpen(false);
          setSecretCopied(false);
          setIssued((res?.data as SelfServiceIssuedCredential) ?? null);
          void refetch();
        },
        onError: (err) => {
          setConfirmOpen(false);
          setError(
            statusOf(err) === 409
              ? t("selfservice.error.noCredential")
              : t("selfservice.error.generate"),
          );
        },
      },
    );
  }

  async function copySecret() {
    if (!issued) {
      return;
    }
    try {
      await navigator.clipboard.writeText(issued.plaintextSecret);
    } catch {
      // Surface inside the dialog (the secret is NOT dropped, so it stays manually copyable).
      setCopyError(t("selfservice.error.copy"));
      return;
    }
    // T9-4: do not leave the secret sitting in component state after copy (keep the non-sensitive
    // clientId so the dialog can confirm which credential was created).
    setCopyError(undefined);
    setIssued((prev) => (prev ? { ...prev, plaintextSecret: "" } : null));
    setSecretCopied(true);
  }

  function closeReveal() {
    setIssued(null);
    setSecretCopied(false);
    setCopyError(undefined);
  }

  function revoke(cred: SelfServiceCredential) {
    setError(undefined);
    mutate(
      { url: `portal/service-account/credentials/${cred.id}/revoke`, method: "post", values: {} },
      {
        onSuccess: () => void refetch(),
        onError: () => setError(t("selfservice.error.revoke")),
      },
    );
  }

  const credColumns: Column<SelfServiceCredential>[] = [
    { header: t("admin.sa.cred.clientId"), cell: (c) => c.clientId },
    { header: t("admin.sa.cred.prefix"), cell: (c) => `${c.secretPrefix}…` },
    { header: t("admin.sa.cred.scopes"), cell: (c) => c.scopes || "—" },
    { header: t("admin.sa.cred.created"), cell: (c) => formatDateTime(c.createdAt, locale) },
    { header: t("admin.sa.cred.lastUsed"), cell: (c) => formatDateTime(c.lastUsedAt, locale) },
    { header: t("admin.sa.cred.expires"), cell: (c) => formatDateTime(c.expiresAt, locale) },
    {
      header: t("admin.common.actions"),
      align: "right",
      cell: (c) => (
        <Button variant="secondary" disabled={mutating} onClick={() => revoke(c)}>
          {t("selfservice.credentials.revoke")}
        </Button>
      ),
    },
  ];

  return (
    <div className="fawkes-admin-page">
      {account ? (
        <div className="fawkes-admin__detail">
          <p className="fawkes-portal__muted">{t("selfservice.intro")}</p>
          {/* Identity */}
          <h3 className="fawkes-admin__section">{t("selfservice.identity.title")}</h3>
          <dl className="fawkes-portal__deflist">
            <dt>{t("selfservice.identity.username")}</dt>
            <dd>{account.username}</dd>
            <dt>{t("selfservice.identity.active")}</dt>
            <dd>
              <StatusBadge tone={account.active ? "success" : "neutral"}>
                {t(account.active ? "admin.common.active" : "admin.common.inactive")}
              </StatusBadge>
            </dd>
          </dl>

          {/* Allowed IPs — READ-ONLY (runbook §2: "contact an administrator to change") */}
          <h3 className="fawkes-admin__section">{t("selfservice.allowedIps.title")}</h3>
          <p className="fawkes-portal__muted">{t("selfservice.allowedIps.intro")}</p>
          {account.allowedIps.length === 0 ? (
            <p className="fawkes-portal__muted">{t("selfservice.allowedIps.empty")}</p>
          ) : (
            <ul className="fawkes-portal__list">
              {account.allowedIps.map((ip) => (
                <li key={ip.cidr} className="fawkes-portal__row">
                  <code>{ip.cidr}</code>
                  <span className="fawkes-portal__muted">
                    {t("selfservice.allowedIps.added")}: {formatDateTime(ip.createdAt, locale)}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {/* Credentials — rotate without downtime */}
          <h3 className="fawkes-admin__section">{t("selfservice.credentials.title")}</h3>
          <p className="fawkes-portal__muted">{t("selfservice.credentials.intro")}</p>
          <div className="fawkes-portal__toolbar">
            <Button disabled={mutating} onClick={() => setConfirmOpen(true)}>
              {t("selfservice.credentials.generate")}
            </Button>
          </div>

          <DataTable
            columns={credColumns}
            rows={account.credentials}
            getRowKey={(c) => c.id}
            empty={<span className="fawkes-portal__muted">{t("selfservice.credentials.empty")}</span>}
          />

          {error ? (
            <p className="fawkes-portal__error" role="alert">
              {error}
            </p>
          ) : null}
        </div>
      ) : null}

      {/* Confirm: generate a new credential */}
      <Modal
        open={confirmOpen}
        title={t("selfservice.generate.confirmTitle")}
        onClose={() => setConfirmOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
              {t("admin.common.cancel")}
            </Button>
            <Button disabled={mutating} onClick={mint}>
              {mutating ? t("admin.common.saving") : t("selfservice.generate.confirm")}
            </Button>
          </>
        }
      >
        <p className="fawkes-portal__muted">{t("selfservice.generate.confirmBody")}</p>
      </Modal>

      {/* Reveal-once: the new client_id + secret, shown exactly once (TRANSLATED labels). */}
      <Modal
        open={issued !== null}
        title={t("selfservice.secretOnce.title")}
        onClose={closeReveal}
        footer={
          <Button variant="secondary" onClick={closeReveal}>
            {t("selfservice.secretOnce.close")}
          </Button>
        }
      >
        {issued ? (
          <div className="fawkes-admin__secret" role="status">
            <p className="fawkes-portal__muted">{t("selfservice.secretOnce.intro")}</p>
            <dl className="fawkes-portal__deflist">
              <dt>{t("selfservice.secretOnce.clientId")}</dt>
              <dd>
                <code>{issued.clientId}</code>
              </dd>
            </dl>
            {secretCopied ? (
              <p className="fawkes-portal__muted" role="status">
                {t("selfservice.secretOnce.copied")}
              </p>
            ) : (
              <>
                <dl className="fawkes-portal__deflist">
                  <dt>{t("selfservice.secretOnce.secret")}</dt>
                  <dd>
                    <code className="fawkes-admin__secret-value">{issued.plaintextSecret}</code>
                  </dd>
                </dl>
                <p className="fawkes-portal__muted">{t("selfservice.secretOnce.warning")}</p>
                <Button variant="secondary" onClick={() => void copySecret()}>
                  {t("selfservice.secretOnce.copy")}
                </Button>
                {copyError ? (
                  <p className="fawkes-portal__error" role="alert">
                    {copyError}
                  </p>
                ) : null}
              </>
            )}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
